import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Mail, Phone, MapPin, ChevronRight, ChevronDown } from 'lucide-react';
import axios from 'axios';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [openAccordion, setOpenAccordion] = useState(null);

  const toggleAccordion = (section) => {
    setOpenAccordion(openAccordion === section ? null : section);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setMessage({ type: 'error', text: 'Lütfen geçerli bir e-posta adresi girin.' });
      return;
    }

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3333/api';
      const response = await axios.post(`${API_URL}/newsletter/subscribe`, { email });

      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
        setEmail('');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Bir hata oluştu. Lütfen tekrar deneyin.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
    }
  };

  return (
    <footer className="bg-[#1a1a1a] text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12 lg:py-16">
        <div className="flex flex-col lg:grid lg:grid-cols-5 gap-0 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1 mb-6 lg:mb-0 border-b border-white/10 lg:border-none pb-8 lg:pb-0 flex flex-col items-center text-center lg:items-start lg:text-left">
            <Link to="/" onClick={() => window.scrollTo(0, 0)} className="inline-block mb-6">
              <img src="/carmen-gold-logo.png" alt="Carmen Gold" 
                width="256" height="64"
                className="h-16 w-auto object-contain" />
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Kuyumcukent'in güvenilir kuyumcusu. Kaliteli altın ve gümüş ürünleriyle hizmetinizdeyiz.
            </p>
            <div className="flex gap-3 justify-center lg:justify-start">
              <a href="https://www.instagram.com/carmengoldkiymetlimadenler/" target="_blank" rel="noopener noreferrer"
                aria-label="Instagram'da Carmen Gold"
                className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center rounded-full
                           hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all duration-300 group">
                <Instagram className="w-4 h-4 text-[#D4AF37] group-hover:text-[#1a1a1a]" />
              </a>
              <a href="https://www.tiktok.com/@carmengoldkm" target="_blank" rel="noopener noreferrer"
                aria-label="TikTok'ta Carmen Gold"
                className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center rounded-full
                           hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all duration-300 group">
                <svg className="w-4 h-4 text-[#D4AF37] group-hover:text-[#1a1a1a]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="border-b border-white/10 lg:border-none py-4 lg:py-0">
            <div
              className="flex justify-between items-center cursor-pointer lg:cursor-default"
              onClick={() => toggleAccordion('quick_links')}
            >
              <h3 className="font-['Cormorant_Garamond'] text-lg font-bold mb-0 lg:mb-6 relative inline-block text-[#D4AF37] uppercase tracking-wider">
                Hızlı Erişim
                <span className="hidden lg:block absolute -bottom-2 left-0 w-8 h-0.5 bg-[#D4AF37]"></span>
              </h3>
              <ChevronDown className={`w-5 h-5 text-[#D4AF37] lg:hidden transition-transform duration-300 ${openAccordion === 'quick_links' ? 'rotate-180' : ''}`} />
            </div>
            <div className={`grid transition-all duration-300 ease-in-out ${openAccordion === 'quick_links' ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'} lg:grid-rows-[1fr] lg:opacity-100 lg:mt-0`}>
              <div className="overflow-hidden">
                <ul className="space-y-3">
                  <li>
                    <Link to="/hakkimizda" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Hakkımızda Sayfası</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/sarrafiye/gram-kulce-altin&ziynet-altin" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Altın Ürünler</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/sarrafiye/gram-kulce-gumus" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Gümüş Ürünler</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/taki-ve-mucevherat" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Takı ve Mücevherat</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/canli-piyasalar" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Canlı Borsalar</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/cok-satanlar" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Çok Satanlar</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Blog</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Customer Service */}
          <div className="border-b border-white/10 lg:border-none py-4 lg:py-0">
            <div
              className="flex justify-between items-center cursor-pointer lg:cursor-default"
              onClick={() => toggleAccordion('customer_service')}
            >
              <h3 className="font-['Cormorant_Garamond'] text-lg font-bold mb-0 lg:mb-6 relative inline-block text-[#D4AF37] uppercase tracking-wider">
                Müşteri Hizmetleri
                <span className="hidden lg:block absolute -bottom-2 left-0 w-8 h-0.5 bg-[#D4AF37]"></span>
              </h3>
              <ChevronDown className={`w-5 h-5 text-[#D4AF37] lg:hidden transition-transform duration-300 ${openAccordion === 'customer_service' ? 'rotate-180' : ''}`} />
            </div>
            <div className={`grid transition-all duration-300 ease-in-out ${openAccordion === 'customer_service' ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'} lg:grid-rows-[1fr] lg:opacity-100 lg:mt-0`}>
              <div className="overflow-hidden">
                <ul className="space-y-3">
                  <li>
                    <a
                      href="/profil"
                      className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group"
                    >
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Hesabım</span>
                    </a>
                  </li>
                  <li>
                    <Link to="/siparis-takibi" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Sipariş Takibi</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/iletisim" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>İade ve Değişim</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/sss" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>SSS</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/video-egitimleri" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Video Eğitimleri</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Legal */}
          <div className="border-b border-white/10 lg:border-none py-4 lg:py-0">
            <div
              className="flex justify-between items-center cursor-pointer lg:cursor-default"
              onClick={() => toggleAccordion('legal')}
            >
              <h3 className="font-['Cormorant_Garamond'] text-lg font-bold mb-0 lg:mb-6 relative inline-block text-[#D4AF37] uppercase tracking-wider">
                Yasal
                <span className="hidden lg:block absolute -bottom-2 left-0 w-8 h-0.5 bg-[#D4AF37]"></span>
              </h3>
              <ChevronDown className={`w-5 h-5 text-[#D4AF37] lg:hidden transition-transform duration-300 ${openAccordion === 'legal' ? 'rotate-180' : ''}`} />
            </div>
            <div className={`grid transition-all duration-300 ease-in-out ${openAccordion === 'legal' ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'} lg:grid-rows-[1fr] lg:opacity-100 lg:mt-0`}>
              <div className="overflow-hidden">
                <ul className="space-y-3">
                  <li>
                    <Link to="/legal/kvkk-aydinlatma" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>KVKK</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/legal/gizlilik-politikasi" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Gizlilik Sözleşmesi</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/legal/cerez-politikasi" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Çerezler</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/legal/kullanim-kosullari" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Koşullar</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/legal/iade-iptal-kosullari" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Teslimat ve İade Şartları</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/legal/mesafeli-satis-sozlesmesi" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Mesafeli Satış Sözleşmesi</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/legal/on-bilgilendirme-formu" className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      <span>Ön Bilgilendirme Formu</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="py-4 lg:py-0">
            <div
              className="flex justify-between items-center cursor-pointer lg:cursor-default"
              onClick={() => toggleAccordion('contact')}
            >
              <h3 className="font-['Cormorant_Garamond'] text-lg font-bold mb-0 lg:mb-6 relative inline-block text-[#D4AF37] uppercase tracking-wider">
                İletişim
                <span className="hidden lg:block absolute -bottom-2 left-0 w-8 h-0.5 bg-[#D4AF37]"></span>
              </h3>
              <ChevronDown className={`w-5 h-5 text-[#D4AF37] lg:hidden transition-transform duration-300 ${openAccordion === 'contact' ? 'rotate-180' : ''}`} />
            </div>
            <div className={`grid transition-all duration-300 ease-in-out ${openAccordion === 'contact' ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'} lg:grid-rows-[1fr] lg:opacity-100 lg:mt-0`}>
              <div className="overflow-hidden">
                <ul className="space-y-3">
                  <li>
                    <a href="tel:+905458897034" className="flex items-start gap-2 text-white/60 hover:text-[#D4AF37] transition-colors group">
                      <Phone className="w-4 h-4 mt-0.5 text-[#D4AF37]" />
                      <div>
                        <p className="text-sm font-medium text-white">+90 545 889 70 34</p>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a href="mailto:info@carmengold.com" className="flex items-start gap-2 text-white/60 hover:text-[#D4AF37] transition-colors group">
                      <Mail className="w-4 h-4 mt-0.5 text-[#D4AF37]" />
                      <div>
                        <p className="text-sm font-medium text-white">info@carmengold.com</p>
                      </div>
                    </a>
                  </li>
                  <li>
                    <div className="flex items-start gap-2 text-white/60">
                      <MapPin className="w-4 h-4 mt-0.5 text-[#D4AF37]" />
                      <div>
                        <p className="text-sm">Kuyumcukent Atolye Bloğu, Zemin Kat</p>
                        <p className="text-sm">10.Sk, No:7, Yenibosna/Bahçelievler/İSTANBUL</p>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter */}
        <div className="mt-6 lg:mt-16 pt-6 lg:pt-12 border-t border-[#D4AF37]/20">
          <div className="max-w-xl mx-auto text-center">
            <div className="flex items-center justify-center gap-4 mb-3">
              <img src="/carmen-gold-logo-small.png" alt="Logo" className="w-12 h-12 object-contain" />
              <h4 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#D4AF37]">Bültenimize Abone Olun</h4>
            </div>
            <p className="text-white/60 text-sm mb-6">Kampanyalar ve yeni ürünlerden haberdar olun</p>

            {/* Success/Error Message */}
            {message.text && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success'
                ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                : 'bg-red-500/10 text-red-400 border border-red-500/20'
                }`}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleNewsletterSubmit} className="flex gap-0">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="E-posta adresiniz"
                required
                disabled={isSubmitting}
                className="flex-1 px-5 py-3.5 bg-white/5 border border-[#D4AF37]/30 text-white 
                         placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37] 
                         transition-colors text-sm rounded-l-full disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-3.5 bg-[#D4AF37] text-[#1a1a1a] font-medium text-sm 
                         uppercase tracking-wider hover:bg-[#F4E4C1] transition-colors rounded-r-full
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Gönderiliyor...' : 'Abone Ol'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#D4AF37]/20">
        <div className="container mx-auto px-4 py-6">
          {/* Payment Logos & ETBİS */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
            <img 
              src="/images/payment/logo_band_white.svg" 
              alt="iyzico ile güvenli ödeme - Visa, MasterCard, Troy" 
              className="h-8 sm:h-10 w-auto opacity-70 hover:opacity-100 transition-opacity duration-300" 
            />
            <a
              href="https://etbis.ticaret.gov.tr/tr/SiteSorgulamaSonuc?siteId=d3bf599c-2dc4-4c7d-8f38-eae7e954a115"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-[#D4AF37]/30 rounded-lg text-xs text-white/80 hover:text-[#D4AF37] transition-all group"
              title="T.C. Ticaret Bakanlığı ETBİS Kayıtlı Site Doğrula"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="font-semibold text-[#D4AF37]">ETBİS</span>
              <span className="text-white/60">Kayıtlı Site</span>
            </a>
          </div>
          <p className="text-white/40 text-sm text-center">
            © {currentYear} Carmen Gold. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
